import React, { useCallback, useMemo, useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  ArrowDownTrayIcon,
  PresentationChartBarIcon,
} from "@heroicons/react/24/outline";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";

import {
  inventorySparesDownloadFailedRequest,
  inventorySparesRescanRequest,
} from "../../../../http-request/inventory/inventoryDownloadFile";

import {
  setInventorySpareLotListFilter,
} from "../../../../store/inventory/inventorySpareLotListSlice";

import { CustomDropdown } from "../../../../component/customdropdown/CustomDropdown";
import classes from "./inventorySparesLotListFilterPage.module.css";

const statuses = [
  { id: "", label: "Select Status" },
  { id: "0", label: "Images Missing" },
  { id: "1", label: "Images Available" },
];

export const InventorySparesLotListFilterPage = ({
  inventoryId,
  firstImageExist,
  filters,
}) => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isDownloading, setIsDownloading] = useState(false);
  const [isRescanning, setIsRescanning] = useState(false);

  /** Disable actions when image_exist === 2 */
  const isActionDisabled = useMemo(
    () => firstImageExist === 2,
    [firstImageExist]
  );

  /* =====================
     URL → REDUX SYNC (on load / refresh)
  ===================== */
  useEffect(() => {
    const statusFromUrl = searchParams.get("status");

    if (statusFromUrl !== filters.status) {
      dispatch(
        setInventorySpareLotListFilter({
          status: statusFromUrl,
        })
      );
    }
  }, []); // run once on mount

  /* =====================
     FILTER HANDLER
  ===================== */
  const handleStatusChange = useCallback(
    (_, value) => {
      // Update Redux (source of truth)
      dispatch(
        setInventorySpareLotListFilter({
          status: value || null,
        })
      );

      // Sync URL (optional but nice)
      const params = new URLSearchParams(searchParams);
      value ? params.set("status", value) : params.delete("status");
      setSearchParams(params);
    },
    [dispatch, searchParams, setSearchParams]
  );

  /* =====================
     ACTIONS
  ===================== */
  const handleDownload = useCallback(async () => {
    if (!inventoryId) return toast.error("Invalid inventory ID");
    if (isActionDisabled) return;

    try {
      setIsDownloading(true);

      const fileData = await inventorySparesDownloadFailedRequest({
        category: "spares",
        requestId: inventoryId,
      });

      const blob = new Blob([fileData], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `mobiGarage_${inventoryId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Download completed!");
    } catch (err) {
      console.error(err);
      toast.error("Error while downloading");
    } finally {
      setIsDownloading(false);
    }
  }, [inventoryId, isActionDisabled]);

  const handleRescan = useCallback(async () => {
    if (!inventoryId) return toast.error("Invalid inventory ID");
    if (isActionDisabled) return;

    try {
      setIsRescanning(true);

      await inventorySparesRescanRequest({
        category: "spares",
        requestId: inventoryId,
      });

      toast.success("Rescan completed!");
    } catch (err) {
      console.error(err);
      toast.error("Error while rescanning");
    } finally {
      setIsRescanning(false);
    }
  }, [inventoryId, isActionDisabled]);

  /* =====================
     RENDER
  ===================== */
  return (
    <div className={classes.box}>
      <h1 className={classes.box__title}>
        Inventory Variant List: {inventoryId}
      </h1>

      <div className={classes.box__content}>
        <div className={classes.box__content__filter}>
          <CustomDropdown
            id="status"
            label="Select Status"
            options={statuses}
            selectedId={filters.status ?? ""}
            onSelection={handleStatusChange}
          />
        </div>

        <button
          className={classes.box__btn}
          onClick={handleRescan}
          disabled={isRescanning || isActionDisabled}
          title={isActionDisabled ? "No valid images found" : ""}
        >
          <PresentationChartBarIcon className={classes.icon} />
          {isRescanning ? "Rescanning..." : "Rescan"}
        </button>

        <button
          className={classes.box__btn}
          onClick={handleDownload}
          disabled={isDownloading || isActionDisabled}
          title={isActionDisabled ? "No invalid images available" : ""}
        >
          <ArrowDownTrayIcon className={classes.icon} />
          {isDownloading ? "Downloading..." : "Download Invalid Images"}
        </button>
      </div>
    </div>
  );
};
